import React from "react";
import "../../../styles/components/FilterResults.css";
import { useFilterEdit, isFilterEditable } from "../contexts/FilterEditContext";
import {
  FILTER_GROUPS,
  FILTER_TYPES,
  getFilterVariant,
} from "../../../utils/filterColors";
import { callOperation } from "../../../utils/apiHelper.js";

import LoadingSpinner from "../../common/LoadingSpinner";

const filterModelToString = (filterModel) => {
  // For attribute filters, show the original_input (optimized string)
  if (filterModel?.original_input) {
    return filterModel.original_input;
  }
  
  const expression = filterModel?.expression || "";
  // Remove any newlines and normalize whitespace
  return expression.replace(/\s+/g, " ").trim();
};

const truncateText = (text, maxLength = 200) => {
  if (!text) return text;
  let textStr = Array.isArray(text) ? text.join(", ") : String(text);
  // Remove any newlines and normalize whitespace
  textStr = textStr.replace(/\s+/g, " ").trim();
  return textStr.length > maxLength
    ? textStr.substring(0, maxLength) + "..."
    : textStr;
};

const groupFilters = (filters) => {
  const grouped = {};

  filters.forEach((filter, originalIndex) => {
    const filterGroup = FILTER_TYPES[filter.type]?.group;
    if (!grouped[filterGroup]) {
      grouped[filterGroup] = [];
    }
    grouped[filterGroup].push({ ...filter, originalIndex });
  });

  return grouped;
};

const FilterResults = (props) => {
  const { editFilter } = useFilterEdit();
  const hasFilters = props.filter_results.length > 0;
  const groupCounts = props.group_counts || {};
  const token = props.token;

  // Initialize with correct defaults to match backend
  const [groupLogic, setGroupLogic] = React.useState({
    position: false, // Genomic Range OR Gene Symbol (default OR)
    row: true, // Attribute logic (default AND)
  });
  const [betweenGroupLogic, setBetweenGroupLogic] = React.useState(true); // Between groups: default AND
  
  // Loading states for logic toggles
  const [loadingStates, setLoadingStates] = React.useState({
    position: false,
    row: false,
    between: false
  });

  // Loading state for filter removal
  const [removingFilters, setRemovingFilters] = React.useState({});

  const handleRemoveFilter = async (filterType) => {
    setRemovingFilters(prev => ({ ...prev, [filterType]: true }));
    try {
      await props.deleteFilter(filterType);
    } finally {
      setRemovingFilters(prev => ({ ...prev, [filterType]: false }));
    }
  };

  // Sync frontend state with backend on component mount
  React.useEffect(() => {
    const fetchLogicState = async () => {
      try {
        const response = await callOperation("get_filter_logic", {}, token);
        const data = response.data;
        // Map backend response format to frontend state
        if (data.genomic_gene_logic !== undefined || data.attr_logic !== undefined) {
          setGroupLogic({
            position: data.genomic_gene_logic ?? false,
            row: data.attr_logic ?? true
          });
        }
        if (data.position_row_logic !== undefined) {
          setBetweenGroupLogic(data.position_row_logic);
        }
      } catch (error) {
        console.warn("Failed to fetch filter logic state:", error);
      }
    };

    if (hasFilters) {
      fetchLogicState();
    }
  }, [hasFilters, token]);

  const logicToString = (isAnd) => (isAnd ? "AND" : "OR");

  const toggleGroupLogic = async (groupKey) => {
    // Map frontend groupKey to backend logic key
    const logicKey = groupKey === "location" ? "position" : "row";
    
    setLoadingStates(prev => ({ ...prev, [logicKey]: true }));
    
    try {
      // Use correct defaults: position defaults to false (OR), row defaults to true (AND)
      const currentValue =
        logicKey === "position"
          ? groupLogic.position ?? false
          : groupLogic.row ?? true;

      const newGroupLogic = {
        ...groupLogic,
        [logicKey]: !currentValue,
      };
      setGroupLogic(newGroupLogic);

      // Call backend to update filter logic (using binary: true = AND, false = OR)
      if (props.updateFilterLogic) {
        await props.updateFilterLogic({
          genomic_gene_logic: newGroupLogic.position ?? false,
          attr_logic: newGroupLogic.row ?? true,
          position_row_logic: betweenGroupLogic,
        });
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, [logicKey]: false }));
    }
  };

  const getGroupLogic = (groupKey) => {
    // Map frontend groupKey to backend logic key
    const logicKey = groupKey === "location" ? "position" : "row";
    // Use correct defaults: position defaults to false (OR), row defaults to true (AND)
    const value =
      logicKey === "position"
        ? groupLogic.position ?? false
        : groupLogic.row ?? true;
    return logicToString(value);
  };

  const toggleBetweenGroupLogic = async () => {
    setLoadingStates(prev => ({ ...prev, between: true }));
    
    try {
      const newBetweenLogic = !betweenGroupLogic;
      setBetweenGroupLogic(newBetweenLogic);

      // Call backend to update filter logic (using binary: true = AND, false = OR)
      if (props.updateFilterLogic) {
        await props.updateFilterLogic({
          genomic_gene_logic: groupLogic.position ?? false,
          attr_logic: groupLogic.row ?? true,
          position_row_logic: newBetweenLogic,
        });
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, between: false }));
    }
  };

  const renderFilterChip = (filter, index) => {
    // Backend provides clean display text for all filters
    const filterText = filterModelToString(filter["filterModel"]);

    const variant = getFilterVariant(filter.type);

    // Show individual count for all filter types
    const individualCount = filter["individual_rows"] || 0;

    // Use clean approach - only filters with proper type are editable
    const isEditable = isFilterEditable(filter.type);

    return (
      <div
        key={index}
        className={`filter-chip filter-chip-${variant} ${
          isEditable ? "smooth-hover" : ""
        }`}
      >
        <span
          className="filter-results-count"
          title={`Individual matches: ${individualCount?.toLocaleString()}`}
        >
          {individualCount?.toLocaleString()}
        </span>
        <button
          className="filter-remove-btn btn-animate d-flex align-items-center justify-content-center"
          onClick={() => handleRemoveFilter(filter.type)}
          title={`Remove filter: ${filterText}`}
          aria-label={`Remove filter: ${filterText}`}
          disabled={removingFilters[filter.type]}
        >
          {removingFilters[filter.type] ? <LoadingSpinner size="0.8em" /> : "×"}
        </button>
        <div
          className="filter-chip-content"
          onClick={() => isEditable && editFilter(index, filter)}
          style={isEditable ? { cursor: "pointer" } : {}}
          title={
            isEditable
              ? `Click to edit: ${
                  Array.isArray(filterText) ? filterText.join(", ") : filterText
                }`
              : filterText
          }
        >
          <span className="filter-text">{truncateText(filterText)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="filter-results-wrapper">
      {!hasFilters ? (
        <div className="no-filters-message">No active filters</div>
      ) : (
        <div className="filter-chips-container">
          {(() => {
            const grouped = groupFilters(props.filter_results);
            const sortedGroups = Object.entries(grouped).sort(
              ([groupA], [groupB]) =>
                (FILTER_GROUPS[groupA]?.order ?? 999) -
                (FILTER_GROUPS[groupB]?.order ?? 999)
            );

            return sortedGroups.map(([groupKey, filtersInGroup], index) => (
              <React.Fragment key={groupKey}>
                <div className="filter-group">
                  <div className="filter-group-header">
                    <span className="filter-group-title">
                      {FILTER_GROUPS[groupKey]?.title || groupKey}
                    </span>
                    <div className="filter-group-controls">
                      {filtersInGroup.length > 0 && (
                        <span className="group-total-count">
                          {(() => {
                            const countKey =
                              groupKey === "location" ? "position" : "row";
                            // Use current processing count for properties if processing, otherwise use group count
                            const count = groupCounts[countKey] || 0;
                            return count.toLocaleString();
                          })()}
                        </span>
                      )}
                      {filtersInGroup.length > 1 && (
                        <button
                          className="filter-group-logic btn-animate"
                          onClick={() => toggleGroupLogic(groupKey)}
                          title="Click to toggle between OR/AND logic"
                          disabled={loadingStates[groupKey === "location" ? "position" : "row"]}
                        >
                          {loadingStates[groupKey === "location" ? "position" : "row"] ? (
                            <LoadingSpinner size="1em" />
                          ) : (
                            getGroupLogic(groupKey)
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="filter-group-chips">
                    {filtersInGroup
                      .sort(
                        (a, b) =>
                          (FILTER_TYPES[a.type]?.order ?? 999) -
                          (FILTER_TYPES[b.type]?.order ?? 999)
                      )
                      .map((filter) => {
                        return renderFilterChip(filter, filter.originalIndex);
                      })}
                  </div>
                </div>
                {index < sortedGroups.length - 1 && sortedGroups.length > 1 && (
                  <button
                    className="filter-groups-connector btn-animate"
                    onClick={() => toggleBetweenGroupLogic()}
                    title="Click to toggle between AND/OR logic between groups"
                    disabled={loadingStates.between}
                  >
                    {loadingStates.between ? (
                      <LoadingSpinner size="1em" />
                    ) : (
                      logicToString(betweenGroupLogic)
                    )}
                  </button>
                )}
              </React.Fragment>
            ));
          })()}
        </div>
      )}
    </div>
  );
};

export default FilterResults;
